import React, { useContext, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import isEmail from 'validator/lib/isEmail';
import qs from 'qs';
import { isEmpty } from 'lodash-es';
import { Alert, Button, Input } from 'chaoskit/src/components';

import { ReliabilityCalculatorContext } from './Context';
import { getGrade } from '../../pages/reliability-calculator';
import ReliabilityRecommendations from './ReliabilityRecommendations';

export const IntroCopy = ({ grade, summary }) => {
  let heading;
  let blurb;
  let full;

  // Only look at first letter-part of grade
  switch (grade.charAt(0)) {
    case 'A':
      heading = 'Congrats!';
      blurb =
        "Based on your answers about this service, you're in great shape.";
      full =
        "Based on your answers about this service, you're in great shape. Ensure this service maintains high marks by continuing to monitor and measure availability KPIs and running regular Chaos Engineering experiments to ensure the service is resilient to an increasingly wide range of failure scenarios.";
      break;
    case 'B':
      heading = 'Nice work!';
      blurb =
        "Based on your answers, you're well on your way to building a highly reliable service.";
      full =
        "Based on your answers, you're well on your way to building a highly reliable service. Improve this service's grade by monitoring and measuring availability KPIs and running regular Chaos Engineering experiments to ensure the service is resilient to an increasingly wide range of failure scenarios.";
      break;
    case 'C':
      heading = "You're off to a good start.";
      blurb =
        'Based on your answers, there are several opportunities for you to improve the reliability of this service.';
      full =
        "Based on your answers, there are several opportunities for you to improve the reliability of this service. Simple interventions like tuning your monitoring and alerting, running small Chaos Engineering experiments, and establishing error budgets will have a significant impact on this service's grade.";
      break;
    case 'F':
      heading = "There's work left to do!";
      blurb =
        'Based on your answers, immediate steps should be taken to improve the reliability of this service. ';
      full =
        "Based on your answers, immediate steps should be taken to improve the reliability of this service. Simple interventions like tuning your monitoring and alerting, running small Chaos Engineering experiments, and establishing error budgets will have a significant impact on this service's grade.";
      break;
    default:
      heading = "There's work left to do!";
      blurb =
        'Based on your answers, immediate steps should be taken to improve the reliability of this service. ';
      full =
        "Based on your answers, immediate steps should be taken to improve the reliability of this service. Simple interventions like tuning your monitoring and alerting, running small Chaos Engineering experiments, and establishing error budgets will have a significant impact on this service's grade.";
      break;
  }

  return (
    <Fragment>
      <h3>{heading}</h3>
      <hr className="reliabilityCalculator__introCopy-hr" />

      {summary ? (
        <Fragment>
          <p>{blurb}</p>
          <p>
            Submit your email to get your full list of personalized
            recommendations and to see how we calculated your score.
          </p>
        </Fragment>
      ) : (
        <p>{full}</p>
      )}
    </Fragment>
  );
};

IntroCopy.propTypes = {
  grade: PropTypes.string.isRequired,
  summary: PropTypes.bool,
};

const Step11 = ({ queryArgs, location }) => {
  const { state } = useContext(ReliabilityCalculatorContext);
  const [errors, setErrors] = useState({});
  const [inputs, setInputs] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [response, setResponse] = useState('');

  const renderGrade = () => {
    if (!isEmpty(queryArgs)) {
      const {
        step1,
        step2,
        step3,
        step4,
        step5,
        step6,
        step7,
        step8,
        step9,
      } = queryArgs;

      return getGrade(
        step1,
        step2,
        step3,
        step4,
        step5,
        step6,
        step7,
        step8,
        step9
      );
    }

    const {
      step1,
      step2,
      step3,
      step4,
      step5,
      step6,
      step7,
      step8,
      step9,
    } = state;

    return getGrade(
      step1,
      step2,
      step3,
      step4,
      step5,
      step6,
      step7,
      step8,
      step9
    );
  };

  const urlResults = window.btoa(
    unescape(encodeURIComponent(qs.stringify(state)))
  );
  const url = `${location.origin}${location.pathname}?results=${urlResults}&ref=calc`;

  const formId = 'reliability-calculator';

  const handleChange = (name, value) => {
    setInputs(currentInputs => ({ ...currentInputs, [name]: value }));
  };

  const encode = data =>
    Object.keys(data)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
      .join('&');

  const validateForm = () => {
    const formErrors = {};

    if (!inputs.email || (inputs.email && !isEmail(inputs.email)))
      formErrors.email = 'Valid email is required';

    return formErrors;
  };

  const grade = renderGrade();

  const handleFormSubmission = () => {
    const formErrors = validateForm();

    if (!isEmpty(formErrors)) {
      setErrors(formErrors);

      return;
    }

    // Otherwise clear errors and submit form
    setErrors({});
    setLoading(true);

    fetch(location.href, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encode({ 'form-name': formId, ...inputs, url }),
    })
      .then(() => {
        if (window.fbq) {
          window.fbq('track', 'Lead');
        }

        if (window.gtag) {
          window.gtag('event', 'Submit', {
            event_category: formId,
          });

          window.gtag('event', 'conversion', {
            send_to: 'AW-824675656/RzDPCJOejZYBEMianokD',
          });
        }

        if (window.clearbit) {
          window.clearbit.identify(inputs.email, { email: inputs.email });
        }

        setLoading(false);
        setResponse('success');
      })
      .catch(() => {
        setLoading(false);
        setResponse('error');
      });
  };

  return (
    <div className="reliabilityCalculator__cell">
      {response !== 'success' && isEmpty(queryArgs) ? (
        <Fragment>
          <div className="reliabilityCalculator__finalGrade-wrapper">
            <div className="reliabilityCalculator__finalGrade">
              <div className="reliabilityCalculator__finalGrade-letter">
                <span>{grade}</span>
                <h5 className="u-textRegular">Overall Grade</h5>
              </div>
              <div className="reliabilityCalculator__finalGrade-content">
                <IntroCopy grade={grade} summary />
                <h5 className="u-textMedium">Get your full summary</h5>
                <form
                  name={formId}
                  method="post"
                  data-netlify="true"
                  data-netlify-honeypot="bot-field"
                  className="reliabilityCalculator__finalGrade-form"
                >
                  <Input
                    required
                    placeholder="your@email.com"
                    name="email"
                    validationMessage={errors.email}
                    onChange={handleChange}
                  />
                  <Button
                    type="outlinePrimary"
                    loading={isLoading}
                    onClick={handleFormSubmission}
                  >
                    Submit
                  </Button>
                  <input
                    type="text"
                    name="url"
                    defaultValue={url}
                    className="u-hidden"
                  />
                  <input type="hidden" name="form-name" value={formId} />
                  <input
                    name="bot-field"
                    className="u-hidden"
                    onChange={e => handleChange(e.target.name, e.target.value)}
                  />
                </form>
                {response === 'error' && (
                  <Alert type="error">
                    There was an error with your submission. Please try again
                    later.
                  </Alert>
                )}
              </div>
            </div>
          </div>
          <div className="u-textCenter u-mt--large u-contrast">
            <Button
              url="/reliability-calculator"
              target="_blank"
              type="default"
            >
              Test Again
            </Button>
          </div>
        </Fragment>
      ) : (
        <ReliabilityRecommendations url={url} grade={grade} />
      )}
    </div>
  );
};

Step11.propTypes = {
  location: PropTypes.object.isRequired,
  queryArgs: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
};

export default Step11;
